// useSelectionModalState.ts
// - Canonical state: FilterState (useReducer)
// - Actions: semantic action creators for the modal UI
// - Selectors: derived read helpers for dropdowns, counts, summaries
// - Integration: publish derived projections to app state

import * as React from 'react';

import { useSetAtom } from 'jotai';

import {
  selectedImageKeysAtom,
  showPerioChartAtom,
} from '../../freeClaims/state';

// Radiograph subtypes you mentioned; extend as needed.
export type RadiographSubtype = 'BITEWING' | 'PERIAPICAL' | 'PANORAMIC';

/** Per-date filter state. Keys are visit dates (YYYY-MM-DD). */
export type DateFilterState = {
  /**
   * radiographs[subtype][imageId] = selected?
   * Subtype keys match imageType (e.g. BITEWING, PERIAPICAL, PANORAMIC).
   */
  radiographs: Partial<Record<RadiographSubtype, Record<string, boolean>>>;

  /**
   * colorImages[imageId] = selected?
   * If there are none present, it can be {}.
   */
  colorImages: Record<string, boolean>;

  /** FMX is singleton per date; present/selected tracked by booleans. */
  'fmx-present': boolean;
  'fmx-selected': boolean;

  /** Perio chart is singleton per date; present/selected tracked by booleans. */
  'periochart-present': boolean;
  'periochart-selected': boolean;
};

/** Filter state: source of truth for the modal. Keys are visit dates (YYYY-MM-DD). */
export type FilterState = Record<string, DateFilterState>;

/** Top-level "type" buckets used by UI dropdowns. */
export type ImageType = 'radiographs' | 'colorImages' | 'fmx' | 'periochart';

/** Tri-state checkbox model (checked/indeterminate/unchecked + disabled). */
export type TriState = {
  checked: boolean;
  indeterminate: boolean;
  disabled: boolean;
};

/*
 * External projections (what other components consume)
 */

/** Derive selected image keys (e.g. "radiograph-0", "color-1") from filter state. */
function filterStateToSelectedImageKeys(
  filterState: FilterState,
): string[] | null {
  const keys: string[] = [];
  Object.values(filterState).forEach((dateState) => {
    Object.values(dateState.radiographs).forEach((byKey) => {
      Object.entries(byKey).forEach(([key, selected]) => {
        if (selected) keys.push(key);
      });
    });
    Object.entries(dateState.colorImages).forEach(([key, selected]) => {
      if (selected) keys.push(key);
    });
  });
  return keys;
}

/** True if any date has FMX present and selected. */
function filterStateToFmxSelected(filterState: FilterState): boolean {
  return Object.values(filterState).some(
    (dateState) => dateState['fmx-present'] && dateState['fmx-selected'],
  );
}

/** True if any date has perio chart present and selected. */
function filterStateToPerioChartSelected(filterState: FilterState): boolean {
  return Object.values(filterState).some(
    (dateState) =>
      dateState['periochart-present'] && dateState['periochart-selected'],
  );
}

/*
 * Reducer actions
 */
export type FilterAction =
  // Item-level
  | {
      type: 'TOGGLE_RADIOGRAPH_ID';
      date: string;
      subtype: RadiographSubtype;
      id: string;
    }
  | {
      type: 'TOGGLE_COLOR_IMAGE_ID';
      date: string;
      id: string;
    }
  | {
      type: 'TOGGLE_FMX';
      date: string;
    }
  | {
      type: 'TOGGLE_PERIOCHART';
      date: string;
    }
  | {
      type: 'SET_FMX';
      date: string;
      selected: boolean;
    }
  | {
      type: 'SET_PERIOCHART';
      date: string;
      selected: boolean;
    }
  | {
      type: 'SET_COLOR_IMAGES';
      date: string;
      selected: boolean;
    }
  // Subtype/type-level
  | {
      type: 'SET_RADIOGRAPH_SUBTYPE';
      date: string;
      subtype: RadiographSubtype;
      selected: boolean;
    }
  // Date-level
  | {
      type: 'SET_DATE';
      date: string;
      selected: boolean;
    }
  | {
      type: 'SET_DATES';
      dates: string[];
      selected: boolean;
    }
  // Global helpers
  | { type: 'CLEAR_ALL' }
  | { type: 'RESET_TO_INITIAL'; payload: FilterState }
  // Additional requested actions
  | { type: 'SELECT_ALL_TYPES'; selected: boolean } // apply across all dates & types
  | { type: 'SELECT_ALL_DATES'; selected: boolean }; // apply across all dates (same as setDates(allDates,...), but explicit)

/* ============================================================================
 * Reducer helpers
 * ==========================================================================*/

function emptyDateState(): DateFilterState {
  return {
    radiographs: {},
    colorImages: {},
    'fmx-present': false,
    'fmx-selected': false,
    'periochart-present': false,
    'periochart-selected': false,
  };
}

function getOrEmpty(dateState: DateFilterState | undefined): DateFilterState {
  return dateState ?? emptyDateState();
}

/** set all images on one date to selected. */
function setDateSelected(
  dateState: DateFilterState,
  selected: boolean,
): DateFilterState {
  const radiographs: Partial<
    Record<RadiographSubtype, Record<string, boolean>>
  > = {};
  Object.entries(dateState.radiographs).forEach(([typeKey, byKey]) => {
    radiographs[typeKey as RadiographSubtype] = Object.fromEntries(
      Object.keys(byKey).map((k) => [k, selected]),
    );
  });
  const colorImages: Record<string, boolean> = {};
  Object.keys(dateState.colorImages).forEach((k) => {
    colorImages[k] = selected;
  });
  return {
    ...dateState,
    radiographs,
    colorImages,
    'fmx-selected': dateState['fmx-present']
      ? selected
      : dateState['fmx-selected'],
    'periochart-selected': dateState['periochart-present']
      ? selected
      : dateState['periochart-selected'],
  };
}

/*
 * Reducer
 */

function filterReducer(state: FilterState, action: FilterAction): FilterState {
  switch (action.type) {
    case 'TOGGLE_RADIOGRAPH_ID': {
      const dateState: DateFilterState = state[action.date];
      if (!dateState) return state;
      const subtypeKey: RadiographSubtype = action.subtype;
      const byKey = dateState.radiographs[subtypeKey];
      if (!byKey || !(action.id in byKey)) return state;
      const next = { ...state };
      const nextDate = {
        ...dateState,
        radiographs: { ...dateState.radiographs },
      };
      nextDate.radiographs[subtypeKey as RadiographSubtype] = {
        ...byKey,
        [action.id]: !byKey[action.id],
      };
      next[action.date] = nextDate;
      return next;
    }

    case 'TOGGLE_COLOR_IMAGE_ID': {
      const dateState = state[action.date];
      if (!dateState || !(action.id in dateState.colorImages)) return state;
      const next = { ...state };
      next[action.date] = {
        ...dateState,
        colorImages: {
          ...dateState.colorImages,
          [action.id]: !dateState.colorImages[action.id],
        },
      };
      return next;
    }

    case 'TOGGLE_FMX': {
      const dateState = state[action.date];
      if (!dateState || !dateState['fmx-present']) return state;
      const next = { ...state };
      next[action.date] = {
        ...dateState,
        'fmx-selected': !dateState['fmx-selected'],
      };
      return next;
    }

    case 'TOGGLE_PERIOCHART': {
      const dateState = state[action.date];
      if (!dateState || !dateState['periochart-present']) return state;
      const next = { ...state };
      next[action.date] = {
        ...dateState,
        'periochart-selected': !dateState['periochart-selected'],
      };
      return next;
    }

    case 'SET_FMX': {
      const dateState = state[action.date];
      if (!dateState || !dateState['fmx-present']) return state;
      const next = { ...state };
      next[action.date] = {
        ...dateState,
        'fmx-selected': action.selected,
      };
      return next;
    }

    case 'SET_PERIOCHART': {
      const dateState = state[action.date];
      if (!dateState || !dateState['periochart-present']) return state;
      const next = { ...state };
      next[action.date] = {
        ...dateState,
        'periochart-selected': action.selected,
      };
      return next;
    }

    case 'SET_COLOR_IMAGES': {
      const dateState = state[action.date];
      if (!dateState) return state;
      const next = { ...state };
      next[action.date] = {
        ...dateState,
        colorImages: Object.fromEntries(
          Object.keys(dateState.colorImages).map((k) => [k, action.selected]),
        ),
      };
      return next;
    }

    case 'SET_RADIOGRAPH_SUBTYPE': {
      const dateState = state[action.date];
      const subtypeKey = action.subtype;
      const byKey = dateState?.radiographs[subtypeKey];
      if (!byKey) return state;
      const next = { ...state };
      const prevDate = getOrEmpty(state[action.date]);
      next[action.date] = {
        ...prevDate,
        radiographs: {
          ...prevDate.radiographs,
          [subtypeKey]: Object.fromEntries(
            Object.keys(byKey).map((k) => [k, action.selected]),
          ),
        },
      };
      return next;
    }

    case 'SET_DATE': {
      const dateState = state[action.date];
      if (!dateState) return state;
      const next = { ...state };
      next[action.date] = setDateSelected(dateState, action.selected);
      return next;
    }

    case 'SET_DATES': {
      let next = state;
      action.dates.forEach((date) => {
        const dateState = next[date];
        if (dateState) {
          next = {
            ...next,
            [date]: setDateSelected(dateState, action.selected),
          };
        }
      });
      return next;
    }

    case 'SELECT_ALL_TYPES': {
      const next = { ...state };
      Object.keys(state).forEach((date) => {
        next[date] = setDateSelected(getOrEmpty(state[date]), action.selected);
      });
      return next;
    }

    case 'SELECT_ALL_DATES': {
      const dates = Object.keys(state);
      let next = state;
      dates.forEach((date) => {
        const dateState = next[date];
        if (dateState) {
          next = {
            ...next,
            [date]: setDateSelected(dateState, action.selected),
          };
        }
      });
      return next;
    }

    case 'CLEAR_ALL': {
      const next: FilterState = {};
      Object.entries(state).forEach(([visitKey, dateState]) => {
        const radiographs: Record<string, Record<string, boolean>> = {};
        Object.entries(dateState.radiographs).forEach(([typeKey, byKey]) => {
          radiographs[typeKey] = Object.fromEntries(
            Object.keys(byKey).map((k) => [k, false]),
          );
        });
        const colorImages: Record<string, boolean> = {};
        Object.keys(dateState.colorImages).forEach((k) => {
          colorImages[k] = false;
        });
        next[visitKey] = {
          radiographs,
          colorImages,
          'fmx-selected': false,
          'fmx-present': dateState['fmx-present'] ?? false,
          'periochart-present': dateState['periochart-present'] ?? false,
          'periochart-selected': false,
        };
      });
      return next;
    }

    case 'RESET_TO_INITIAL':
      return action.payload;

    default:
      return state;
  }
}

/*
 * Selectors (pure helpers)
 */

/** Count how many selectable "items" are present on a date. */
function getDatePresentCount(filterState: FilterState, date: string): number {
  const ds = filterState[date];
  if (!ds) return 0;
  let n = 0;
  Object.values(ds.radiographs).forEach((byKey) => {
    n += Object.keys(byKey).length;
  });
  n += Object.keys(ds.colorImages).length;
  if (ds['fmx-present']) n += 1;
  if (ds['periochart-present']) n += 1;
  return n;
}

/** Count how many selectable "items" are selected on a date. */
function getDateSelectedCount(filterState: FilterState, date: string): number {
  const ds = filterState[date];
  if (!ds) return 0;
  let n = 0;
  Object.values(ds.radiographs).forEach((byKey) => {
    Object.values(byKey).forEach((v) => {
      if (v) n += 1;
    });
  });
  Object.values(ds.colorImages).forEach((v) => {
    if (v) n += 1;
  });
  if (ds['fmx-present'] && ds['fmx-selected']) n += 1;
  if (ds['periochart-present'] && ds['periochart-selected']) n += 1;
  return n;
}

/** Date tri-state for dropdown checkbox: checked iff ALL present items are selected. */
function getDateTriState(filterState: FilterState, date: string): TriState {
  const present = getDatePresentCount(filterState, date);
  const selected = getDateSelectedCount(filterState, date);
  const disabled = present === 0;
  const checked = present > 0 && selected === present;
  const indeterminate = selected > 0 && selected < present;
  return { checked, indeterminate, disabled };
}

/** "Present types" for a date: which buckets exist at all. */
function getPresentTypesByDate(
  filterState: FilterState,
  date: string,
): ImageType[] {
  const ds = filterState[date];
  if (!ds) return [];
  const out: ImageType[] = [];
  const hasRadiographs = Object.values(ds.radiographs).some(
    (byKey) => Object.keys(byKey).length > 0,
  );
  if (hasRadiographs) out.push('radiographs');
  if (Object.keys(ds.colorImages).length > 0) out.push('colorImages');
  if (ds['fmx-present']) out.push('fmx');
  if (ds['periochart-present']) out.push('periochart');
  return out;
}

/** Radiograph subtypes present on a date. */
function getPresentRadiographSubtypes(
  filterState: FilterState,
  date: string,
): RadiographSubtype[] {
  const ds = filterState[date];
  if (!ds) return [];
  const rad = ds.radiographs as Record<string, Record<string, boolean>>;
  return Object.keys(rad).filter(
    (sk) => Object.keys(rad[sk]).length > 0,
  ) as RadiographSubtype[];
}

/** Radiograph counts for a subtype on a date. */
function getRadiographCounts(
  filterState: FilterState,
  date: string,
  subtype: RadiographSubtype,
): { present: number; selected: number } {
  const ds = filterState[date];
  const subtypeKey = subtype as string;
  const byKey = ds
    ? (ds.radiographs as Record<string, Record<string, boolean>>)[subtypeKey]
    : undefined;
  if (!byKey) return { present: 0, selected: 0 };
  const present = Object.keys(byKey).length;
  const selected = Object.values(byKey).filter(Boolean).length;
  return { present, selected };
}

/** Color image counts on a date. */
function getColorImageCounts(
  filterState: FilterState,
  date: string,
): { present: number; selected: number } {
  const ds = filterState[date];
  if (!ds) return { present: 0, selected: 0 };
  const present = Object.keys(ds.colorImages).length;
  const selected = Object.values(ds.colorImages).filter(Boolean).length;
  return { present, selected };
}

/** Singleton flags on a date. */
function getFmxState(
  filterState: FilterState,
  date: string,
): { present: boolean; selected: boolean } {
  const ds = filterState[date];
  if (!ds) return { present: false, selected: false };
  return {
    present: ds['fmx-present'] ?? false,
    selected: ds['fmx-selected'] ?? false,
  };
}

function getPeriochartState(
  filterState: FilterState,
  date: string,
): { present: boolean; selected: boolean } {
  const ds = filterState[date];
  if (!ds) return { present: false, selected: false };
  return {
    present: ds['periochart-present'] ?? false,
    selected: ds['periochart-selected'] ?? false,
  };
}

function getSelectedDates(filterState: FilterState): string[] {
  return Object.keys(filterState).filter(
    (date) => getDateSelectedCount(filterState, date) > 0,
  );
}

/** Radiograph subtype: total present and selected across all dates. */
function getRadiographSubtypeCountsAllDates(
  filterState: FilterState,
  subtype: RadiographSubtype,
): { present: number; selected: number } {
  let present = 0;
  let selected = 0;
  Object.keys(filterState).forEach((date) => {
    const c = getRadiographCounts(filterState, date, subtype);
    present += c.present;
    selected += c.selected;
  });
  return { present, selected };
}

/** Radiograph subtype tri-state across all dates. */
function getRadiographSubtypeTriState(
  filterState: FilterState,
  subtype: RadiographSubtype,
): TriState {
  const { present, selected } = getRadiographSubtypeCountsAllDates(
    filterState,
    subtype,
  );
  const disabled = present === 0;
  const checked = present > 0 && selected === present;
  const indeterminate = selected > 0 && selected < present;
  return { checked, indeterminate, disabled };
}

/** FMX: count of dates where present, and where present and selected. */
function getFmxCountsAllDates(filterState: FilterState): {
  present: number;
  selected: number;
} {
  let present = 0;
  let selected = 0;
  Object.keys(filterState).forEach((date) => {
    const s = getFmxState(filterState, date);
    if (s.present) {
      present += 1;
      if (s.selected) selected += 1;
    }
  });
  return { present, selected };
}

/** FMX tri-state across all dates. */
function getFmxTriState(filterState: FilterState): TriState {
  const { present, selected } = getFmxCountsAllDates(filterState);
  const disabled = present === 0;
  const checked = present > 0 && selected === present;
  const indeterminate = selected > 0 && selected < present;
  return { checked, indeterminate, disabled };
}

/** Perio chart: count of dates where present, and where present and selected. */
function getPeriochartCountsAllDates(filterState: FilterState): {
  present: number;
  selected: number;
} {
  let present = 0;
  let selected = 0;
  Object.keys(filterState).forEach((date) => {
    const s = getPeriochartState(filterState, date);
    if (s.present) {
      present += 1;
      if (s.selected) selected += 1;
    }
  });
  return { present, selected };
}

/** Perio chart tri-state across all dates. */
function getPeriochartTriState(filterState: FilterState): TriState {
  const { present, selected } = getPeriochartCountsAllDates(filterState);
  const disabled = present === 0;
  const checked = present > 0 && selected === present;
  const indeterminate = selected > 0 && selected < present;
  return { checked, indeterminate, disabled };
}

/** Color images: total present and selected across all dates. */
function getColorImageCountsAllDates(filterState: FilterState): {
  present: number;
  selected: number;
} {
  let present = 0;
  let selected = 0;
  Object.keys(filterState).forEach((date) => {
    const c = getColorImageCounts(filterState, date);
    present += c.present;
    selected += c.selected;
  });
  return { present, selected };
}

/** Color images tri-state across all dates. */
function getColorImagesTriState(filterState: FilterState): TriState {
  const { present, selected } = getColorImageCountsAllDates(filterState);
  const disabled = present === 0;
  const checked = present > 0 && selected === present;
  const indeterminate = selected > 0 && selected < present;
  return { checked, indeterminate, disabled };
}

/* ============================================================================
 * Hook: public API
 * ==========================================================================*/

export type UseSelectionModalStateOptions = {
  /** Initial canonical state for the modal. */
  initialFilterState: FilterState;

  /**
   * If true, will publish derived projections to external atoms whenever filterState changes.
   * Default: true.
   */
  publishToAtoms?: boolean;
  setShowFMX: (show: boolean) => void;
};

export type SelectionModalActions = {
  // Item-level
  toggleRadiographId: (
    date: string,
    subtype: RadiographSubtype,
    id: string,
  ) => void;
  toggleColorImageId: (date: string, id: string) => void;
  toggleFmx: (date: string) => void;
  togglePeriochart: (date: string) => void;

  // Subtype/type-level
  setRadiographSubtype: (
    date: string,
    subtype: RadiographSubtype,
    selected: boolean,
  ) => void;
  setRadiographSubtypeAllDates: (
    subtype: RadiographSubtype,
    selected: boolean,
  ) => void;
  setFmxAllDates: (selected: boolean) => void;
  setPeriochartAllDates: (selected: boolean) => void;
  setColorImagesAllDates: (selected: boolean) => void;

  // Date-level
  setDate: (date: string, selected: boolean) => void;
  setDates: (dates: string[], selected: boolean) => void;

  // Global helpers
  clearAll: () => void;
  revertToInitial: (state: FilterState) => void;

  // Additional requested global actions
  selectAllTypes: (selected: boolean) => void;
  selectAllDates: (selected: boolean) => void;
};

export type SelectionModalSelectors = {
  // Per-date
  datePresentCount: (date: string) => number;
  dateTriState: (date: string) => TriState;

  presentTypesByDate: (date: string) => ImageType[];

  // Radiographs
  radiographSubtypeTriState: (subtype: RadiographSubtype) => TriState;

  // Color
  colorImagesTriState: () => TriState;

  // Singleton (per-date)
  fmxState: (date: string) => { present: boolean; selected: boolean };
  fmxTriState: () => TriState;
  periochartState: (date: string) => { present: boolean; selected: boolean };
  periochartTriState: () => TriState;

  // Global
  selectedDates: string[];
};

export type UseSelectionModalStateResult = {
  filterState: FilterState;
  actions: SelectionModalActions;
  selectors: SelectionModalSelectors;
  onModalClose: () => void;
};

export function useSelectionModalState(
  opts: UseSelectionModalStateOptions,
): UseSelectionModalStateResult {
  const { initialFilterState, publishToAtoms = true } = opts;

  const [filterState, dispatch] = React.useReducer(
    filterReducer,
    initialFilterState,
  );

  const setSelectedImageKeys = useSetAtom(selectedImageKeysAtom);
  const setShowPerioChart = useSetAtom(showPerioChartAtom);

  function onModalClose() {
    if (!publishToAtoms) return;

    // Publish derived views to atoms (one-way sync: canonical filterState -> derived atoms)
    setSelectedImageKeys(filterStateToSelectedImageKeys(filterState));

    const showFMX = filterStateToFmxSelected(filterState);
    opts.setShowFMX(showFMX);

    setShowPerioChart(filterStateToPerioChartSelected(filterState));
  }
  /* ----------------------------
   * Semantic actions (modal calls these)
   * -------------------------- */

  const actions: SelectionModalActions = React.useMemo(
    () => ({
      toggleRadiographId: (date, subtype, id) =>
        dispatch({ type: 'TOGGLE_RADIOGRAPH_ID', date, subtype, id }),

      toggleColorImageId: (date, id) =>
        dispatch({ type: 'TOGGLE_COLOR_IMAGE_ID', date, id }),

      toggleFmx: (date) => dispatch({ type: 'TOGGLE_FMX', date }),

      togglePeriochart: (date) => dispatch({ type: 'TOGGLE_PERIOCHART', date }),

      setRadiographSubtype: (date, subtype, selected) =>
        dispatch({ type: 'SET_RADIOGRAPH_SUBTYPE', date, subtype, selected }),

      setRadiographSubtypeAllDates: (subtype, selected) => {
        Object.keys(filterState).forEach((date) => {
          if (
            getPresentRadiographSubtypes(filterState, date).includes(subtype)
          ) {
            dispatch({
              type: 'SET_RADIOGRAPH_SUBTYPE',
              date,
              subtype,
              selected,
            });
          }
        });
      },

      setFmxAllDates: (selected) => {
        Object.keys(filterState).forEach((date) => {
          if (getFmxState(filterState, date).present) {
            dispatch({ type: 'SET_FMX', date, selected });
          }
        });
      },

      setPeriochartAllDates: (selected) => {
        Object.keys(filterState).forEach((date) => {
          if (getPeriochartState(filterState, date).present) {
            dispatch({ type: 'SET_PERIOCHART', date, selected });
          }
        });
      },

      setColorImagesAllDates: (selected) => {
        Object.keys(filterState).forEach((date) => {
          if (getColorImageCounts(filterState, date).present > 0) {
            dispatch({ type: 'SET_COLOR_IMAGES', date, selected });
          }
        });
      },

      setDate: (date, selected) =>
        dispatch({ type: 'SET_DATE', date, selected }),

      setDates: (dates, selected) =>
        dispatch({ type: 'SET_DATES', dates, selected }),

      clearAll: () => dispatch({ type: 'CLEAR_ALL' }),

      revertToInitial: (state) =>
        dispatch({ type: 'RESET_TO_INITIAL', payload: state }),

      selectAllTypes: (selected) =>
        dispatch({ type: 'SELECT_ALL_TYPES', selected }),

      selectAllDates: (selected) =>
        dispatch({ type: 'SELECT_ALL_DATES', selected }),
    }),
    [filterState],
  );

  /*
   * Selectors (derived read API)
   */

  const selectedDates = React.useMemo(
    () => getSelectedDates(filterState),
    [filterState],
  );

  const selectors: SelectionModalSelectors = React.useMemo(
    () => ({
      // Per-date
      datePresentCount: (date) => getDatePresentCount(filterState, date),
      dateTriState: (date) => getDateTriState(filterState, date),

      presentTypesByDate: (date) => getPresentTypesByDate(filterState, date),

      // Radiographs
      radiographSubtypeTriState: (subtype) =>
        getRadiographSubtypeTriState(filterState, subtype),

      // Color
      colorImagesTriState: () => getColorImagesTriState(filterState),

      // Singleton
      fmxState: (date) => getFmxState(filterState, date),
      fmxTriState: () => getFmxTriState(filterState),
      periochartState: (date) => getPeriochartState(filterState, date),
      periochartTriState: () => getPeriochartTriState(filterState),

      // Global
      selectedDates,
    }),
    [filterState, selectedDates],
  );

  return {
    filterState,
    actions,
    selectors,
    onModalClose,
  };
}
